import { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../store/hooks.ts";
import { updateState } from "../../store/slices/uploadsResponseModalSlice.ts";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import React from "react";

const UploadResponseCardStack = () => {
	const filesStatus = useAppSelector(
		(state) => state.uploadsResponseModal.filesStatus
	);
	const dispatch = useAppDispatch();
	const [pagination, setPagination] = useState({ index: 0, direction: 0 });

	if (!filesStatus || filesStatus.length === 0) {
		return null;
	}

	const paginate = (newDirection: number) => {
		setPagination(({ index }) => ({
			direction: newDirection,
			index:
				(index + newDirection + filesStatus.length) %
				filesStatus.length,
		}));
	};

	const handleConfirm = () => {
		dispatch(updateState(false));
	};

	const variants = {
		enter: () => ({
			scale: 0.95,
			opacity: 0,
			x: 16,
			y: 8,
		}),
		center: {
			scale: 1,
			opacity: 1,
			x: 0,
			y: 0,
		},
		exit: (direction: number) => ({
			x: direction > 0 ? -100 : 100,
			opacity: 0,
		}),
	};

	return (
		<Box
			sx={{
				position: "relative",
				height: "100%", // fallback if size not provided
				width: "100%",
				display: "flex",
				flexDirection: "column", // ✅ stack card + buttons
				alignItems: "center",
				p: 2,
				boxSizing: "border-box",
				overflow: "hidden",
			}}
		>
			{/* ✅ Badge */}
			<Typography
				variant="caption"
				sx={{
					position: "absolute",
					top: 16,
					right: 16,
					backgroundColor: "#E0E0E0",
					color: "#333",
					borderRadius: "12px",
					px: 1.5,
					py: 0.5,
					fontWeight: 500,
					zIndex: 2,
				}}
			>
				{pagination.index + 1} / {filesStatus.length}
			</Typography>

			{/* ✅ AnimatePresence + motion.div for cards */}
			<Box
				sx={{
					position: "relative",
					width: "100%",
					height: "100%",
				}}
			>
				{/* Static 'back' card */}
				{filesStatus.length > 1 && (
					<>
						{filesStatus.length > 2 && (
							<Paper
								elevation={3}
								sx={{
									position: "absolute",
									top: 8,
									left: 24,
									width: "100%",
									height: "90%",
									borderRadius: 2,
									backgroundColor: "#f0f0f0",
									transform: "scale(0.9)",
									zIndex: 0,
								}}
							/>
						)}
						<Paper
							elevation={3}
							sx={{
								position: "absolute",
								top: 8,
								left: 12,
								width: "100%",
								height: "95%",
								borderRadius: 2,
								backgroundColor: "#f0f0f0",
								transform: "scale(0.95)",
								zIndex: 0,
							}}
						/>
					</>
				)}
				<AnimatePresence mode="wait" custom={pagination.direction}>
					<motion.div
						key={pagination.index}
						custom={pagination.direction}
						variants={variants}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{ duration: 0.4 }}
						style={{
							width: "100%",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							borderRadius: 8,
							overflow: "hidden",
							backgroundColor: "transparent",
							zIndex: 1,
						}}
					>
						<Paper
							elevation={6}
							sx={{
								width: "100%",
								height: "100%",
								p: 3,
								borderRadius: 2,
								textAlign: "center",
								overflowY: "auto",
								boxSizing: "border-box",
								display: "flex",
								flexDirection: "column",
								justifyContent: "space-between",
								backgroundColor:
									filesStatus[pagination.index].status ===
									"success"
										? "#E8F5E9"
										: "#FFEBEE",
							}}
						>
							{/* Content */}
							<Box>
								<Typography variant="h6" sx={{ mb: 1 }}>
									{filesStatus[pagination.index].file}
								</Typography>
								{filesStatus[pagination.index].message && (
									<Typography variant="body2" sx={{ mb: 2 }}>
										{filesStatus[pagination.index].message}
									</Typography>
								)}
							</Box>

							{/* In-card nav buttons */}
							<Stack
								direction="row"
								spacing={2}
								justifyContent="center"
							>
								<Button
									size="small"
									onClick={() => paginate(-1)}
								>
									Previous
								</Button>
								<Button
									size="small"
									onClick={() => paginate(1)}
								>
									Next
								</Button>
							</Stack>
						</Paper>
					</motion.div>
				</AnimatePresence>
			</Box>

			{/* ✅ Buttons below the card, fixed */}
			<Box
				sx={{
					width: "100%",
					display: "flex",
					justifyContent: "center",
					mt: 2,
				}}
			>
				<Button
					size="small"
					disableElevation
					variant="contained"
					onClick={handleConfirm}
				>
					Close All
				</Button>
			</Box>
		</Box>
	);
};

export default UploadResponseCardStack;
